import { useEffect, useState, useCallback, useRef, type Ref } from "react";
import { Link } from "react-router-dom";
import { NEWSROOM_RETURN, newsroomDetailState } from "@/lib/newsroomNavigation";
import { publicApi } from "@/lib/api";
import type { NewsItem } from "@/lib/types";

/** 트랙이 가로로 이동하는 속도(px/s) — 뉴스 개수·텍스트 길이와 무관하게 일정 */
const MARQUEE_SPEED_PX_PER_SEC = 30;

export default function NewsTicker() {
  const [items, setItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [duration, setDuration] = useState(30);
  const trackRef = useRef<HTMLDivElement>(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await publicApi.get<NewsItem[]>("/public/news/news");
      const active = (res.data || []).filter((item) => item.status === "active");
      setItems(active);
    } catch {
      // 데이터 없을 경우 빈 목록 유지
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const isScrolling = items.length >= 2;

  useEffect(() => {
    if (!isScrolling) return;

    const track = trackRef.current;
    if (!track) return;

    const updateDuration = () => {
      const width = track.offsetWidth;
      if (width > 0) {
        setDuration(width / MARQUEE_SPEED_PX_PER_SEC);
      }
    };

    updateDuration();

    const observer = new ResizeObserver(updateDuration);
    observer.observe(track);
    return () => observer.disconnect();
  }, [isScrolling, items]);

  if (loading || items.length === 0) {
    return null;
  }

  const linkClass =
    "inline-flex items-center text-sm font-medium text-[#2c4964] hover:text-[#1977cc] transition-colors shrink-0";

  const renderTrack = (
    trackKey: number,
    ariaHidden = false,
    ref?: Ref<HTMLDivElement>,
  ) => (
    <div
      key={trackKey}
      ref={ref}
      className="flex shrink-0 items-center"
      aria-hidden={ariaHidden || undefined}
    >
      {items.map((item) => (
        <Link
          key={`${item.id}-${trackKey}`}
          to={`/newsroom/news/${item.id}`}
          state={newsroomDetailState(NEWSROOM_RETURN.home)}
          className={`${linkClass} mx-6`}
          tabIndex={ariaHidden ? -1 : undefined}
        >
          <span className="truncate max-w-[600px]">{item.title}</span>
          <span className="mx-4 text-gray-300">|</span>
        </Link>
      ))}
    </div>
  );

  return (
    <section className="bg-white border-b border-gray-100 py-3 overflow-hidden">
      <div className="container mx-auto px-4 flex items-center gap-3">
        <i className="ri-volume-up-line text-[#1977cc] text-sm shrink-0" />
        <div className="overflow-hidden relative flex-1">
          {isScrolling ? (
            <div
              className="flex w-max whitespace-nowrap hover:[animation-play-state:paused]"
              style={{
                animation: `marquee ${duration}s linear infinite`,
              }}
            >
              {renderTrack(0, false, trackRef)}
              {renderTrack(1, true)}
            </div>
          ) : (
            <div className="flex">
              {items.map((item) => (
                <Link
                  key={item.id}
                  to={`/newsroom/news/${item.id}`}
                  state={newsroomDetailState(NEWSROOM_RETURN.home)}
                  className={linkClass}
                >
                  <span className="truncate max-w-[600px]">{item.title}</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
      {isScrolling && (
        <style>{`
          @keyframes marquee {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
        `}</style>
      )}
    </section>
  );
}
